import React, { useState, useEffect } from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { GridExpandMoreIcon } from "@mui/x-data-grid"
import { Controller, useForm } from "react-hook-form"
import { capitalize, debounce, each, isEqual } from "lodash"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { DateTime } from "luxon"
import { useSnackbar } from "notistack"
import { Cancel, EventBusy, Vignette } from "@mui/icons-material"
import CustomeDatePicker from "../../Component/datePicker.jsx"
import usePartialUpdateEntity from "../../Hooks/usePartialUpdateEntity.js"
import useUserPermissions from "../../Hooks/useUserPermissions.js"
import RestrictedAccess from "../../Component/restrictedAccess.jsx"

export function diff(a, b) {
    const r = {}
    each(a, (v, k) => {
        if (b[k] === v || isEqual(b[k], v)) return
        // but what if it returns an empty object? still attach?
        r[k] = v
    })
    return r
}

function UpdatePreRIDEProgram() {
    const { updatePreRideSpending } = useUserPermissions()
    const { state: preride } = useLocation()

    const {
        handleSubmit,
        setValue,
        formState: { errors },
        watch,
        control,
    } = useForm({
        defaultValues: preride,
    })

    const { tId } = useParams()
    const endPoint = tId ? `team/${tId}/preride` : "preride"
    const [customeWatch, setCustomeWatch] = useState(preride)
    const [expandedAccord, setExpandedAccord] = useState(false)

    // NONE UI STATES
    const navigate = useNavigate()
    const programRoute = tId ? `/departments/${tId}/pre-RIDE` : "/pre-RIDE"

    useEffect(() => {
        const subscription = watch((d) => {
            const tripLim = d.trip_limit_amount
            const spendingLim = d.spending_limit_amount

            // TO DEBOUNCE THE WATCH, AVOIDS RE-RENDER ON EVERY KEYSTROKE
            if (tripLim >= 0 || spendingLim >= 0)
                debounce(() => setCustomeWatch(d), 100)()
        })

        return () => subscription.unsubscribe()
    }, [watch])

    // SERVER STATE AND MUTATIONS
    const { enqueueSnackbar } = useSnackbar()

    const { mutate, isLoading, data, error } = usePartialUpdateEntity(endPoint)

    useEffect(() => {
        if (data) {
            enqueueSnackbar("preRide budget updated successfully", {
                variant: "success",
            })
            navigate(programRoute)
        }
        if (error) {
            const fieldError =
                error.response?.data?.error?.field_error?.[0]?.description
            const errorMessage = error.response?.data?.error?.message
            enqueueSnackbar(fieldError || errorMessage || "Request Failed", {
                variant: "error",
            })
        }
    }, [data, error, enqueueSnackbar, navigate, programRoute])

    // HANDLERS
    const updateBudget = (d) => {
        // BACKEND DOESN'T RECEIVE A `NONE DATEtIME` VALUE FOR EXPIRE DATE
        if (!d.expire_date) {
            const { expire_date: camelCase, ...rest } = d
            mutate({
                ...rest,
                id: d.id,
            })
        } else {
            mutate({
                ...diff(d, preride),
                id: d.id,
            })
        }
    }

    if (!updatePreRideSpending) return <RestrictedAccess />

    return (
        <Container
            maxWidth="false"
            sx={{ pb: { md: "3em", xs: "1em" }, pt: { md: "3em" } }}
        >
            <Grid
                container
                spacing={1}
                justifyContent="space-around"
                sx={{
                    pt: "1.5em",
                    pl: { xs: 0 },
                }}
            >
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={4}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.5em",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: ".5em",
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: "500" }}>
                            Name
                        </Typography>
                        <Controller
                            name="name"
                            rules={{ required: "name is required" }}
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                                <TextField
                                    value={value}
                                    inputRef={ref}
                                    onChange={(e) =>
                                        onChange(e.currentTarget.value)
                                    }
                                    fullWidth
                                    helperText={errors.name?.message}
                                />
                            )}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: ".5em",
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: "500" }}>
                            Description
                        </Typography>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    value={value}
                                    onChange={(e) =>
                                        onChange(e.currentTarget.value)
                                    }
                                    multiline
                                    rows={2}
                                    fullWidth
                                />
                            )}
                        />
                    </Box>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: ".5em",
                    }}
                >
                    <Typography variant="body1" sx={{ fontWeight: "500" }}>
                        Rule
                    </Typography>
                    <Accordion
                        expanded={expandedAccord === "spending_limit"}
                        onChange={() => {
                            if (expandedAccord === "spending_limit") {
                                setExpandedAccord(false)
                            } else {
                                setExpandedAccord("spending_limit")
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <GridExpandMoreIcon color="secondary" />
                            }
                        >
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    display: "flex",
                                }}
                            >
                                <IconButton>
                                    <Vignette />
                                </IconButton>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        Spending Limit
                                    </Typography>
                                    <Typography variant="body2">
                                        {(() => {
                                            const isUnlimited =
                                                customeWatch?.spending_limit ===
                                                false
                                            return isUnlimited
                                                ? "No Limit"
                                                : `${
                                                      customeWatch?.spending_limit_amount
                                                  } ETB ${watch(
                                                      "spending_limit_duration"
                                                  )}`
                                        })()}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                color="secondary"
                                sx={{ fontWeight: "bold", alignSelf: "center" }}
                            >
                                Edit
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="subtitle2">
                                Enter limit amount
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Controller
                                        name="spending_limit_amount"
                                        control={control}
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <TextField
                                                fullWidth
                                                value={value || ""}
                                                onChange={(e) => {
                                                    const val =
                                                        e.currentTarget.value
                                                    onChange(Number(val))
                                                    if (val > 0) {
                                                        setValue(
                                                            "spending_limit",
                                                            true
                                                        )
                                                    } else
                                                        setValue(
                                                            "spending_limit",
                                                            false
                                                        )
                                                }}
                                                type="number"
                                                InputProps={{
                                                    inputProps: {
                                                        min: 1,
                                                        sx: {
                                                            backgroundColor:
                                                                "white",
                                                            mr: ".5em",
                                                        },
                                                    },
                                                    sx: {
                                                        "&.MuiInputBase-root": {
                                                            pr: "0.5em",
                                                            backgroundColor:
                                                                "#dfdfdf",
                                                        },
                                                    },
                                                    endAdornment: (
                                                        <Typography
                                                            sx={{
                                                                backgroundColor:
                                                                    "#dfdfdf",
                                                            }}
                                                        >
                                                            ETB
                                                        </Typography>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Controller
                                        name="spending_limit_duration"
                                        control={control}
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <Autocomplete
                                                value={capitalize(value)}
                                                onChange={(e, newValue) =>
                                                    onChange(
                                                        newValue.toLowerCase()
                                                    )
                                                }
                                                options={[
                                                    "Daily",
                                                    "Monthly",
                                                    "Weekly",
                                                ]}
                                                renderInput={(params) => (
                                                    <TextField {...params} />
                                                )}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        expanded={expandedAccord === "expire_date"}
                        onChange={() => {
                            if (expandedAccord === "expire_date") {
                                setExpandedAccord(false)
                            } else {
                                setExpandedAccord("expire_date")
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <GridExpandMoreIcon color="secondary" />
                            }
                        >
                            <Box sx={{ flexGrow: 1, display: "flex" }}>
                                <IconButton>
                                    <EventBusy />
                                </IconButton>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                    >
                                        Expire Date
                                    </Typography>
                                    <Typography variant="body2">
                                        {watch("has_expire_date")
                                            ? DateTime.fromISO(
                                                  watch("expire_date")
                                              ).toFormat("f")
                                            : "No Expire Date"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                color="secondary"
                                sx={{ fontWeight: "bold", alignSelf: "center" }}
                            >
                                Edit
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2">
                                Enter Expire Date
                            </Typography>
                            <Controller
                                name="expire_date"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <>
                                        <CustomeDatePicker
                                            value={
                                                value !==
                                                    "0001-01-01T00:00:00Z" &&
                                                watch("has_expire_date")
                                                    ? value
                                                    : null
                                            }
                                            onChange={(expDate) => {
                                                if (expDate) {
                                                    setValue(
                                                        "has_expire_date",
                                                        true
                                                    )
                                                    onChange(expDate)
                                                } else {
                                                    setValue(
                                                        "has_expire_date",
                                                        false
                                                    )
                                                }
                                            }}
                                            minDate={DateTime.now().toISO()}
                                        />
                                        <Tooltip title="Clear Expire Date">
                                            <IconButton
                                                size="xl"
                                                onClick={() =>
                                                    setValue(
                                                        "has_expire_date",
                                                        false
                                                    )
                                                }
                                            >
                                                <Cancel size="xl" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                <Grid item xs={12} sm={12} md={11}>
                    <Divider sx={{ mx: "auto", my: ".5em", mt: "1.5em" }} />
                </Grid>
                <Grid item xs={12} sm={12} md={4} />
                <Grid item xs={12} sm={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            margin: "auto",
                        }}
                    >
                        <Button
                            color="common"
                            variant="contained"
                            onClick={() => navigate(programRoute)}
                            sx={{
                                marginLeft: "auto",
                                marginRight: "1em",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            startIcon={
                                isLoading ? (
                                    <CircularProgress
                                        size="1em"
                                        sx={{ color: "white" }}
                                    />
                                ) : null
                            }
                            variant="contained"
                            onClick={handleSubmit(updateBudget)}
                        >
                            Save
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Container>
    )
}

export default UpdatePreRIDEProgram
